import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface WeeklyDataPoint {
  week: string;
  kapanan: number;
  eklenen: number;
  teklif: number;
  ciro: number;
}

export interface DailyDataPoint {
  day: string;
  kapanan: number;
  eklenen: number;
  teklif: number;
  ciro: number;
}

const DAY_LABELS = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

function getWeekNumber(d: Date): number {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function weekLabel(d: Date): string {
  return `Hf ${getWeekNumber(d)}`;
}

export function useWeeklyStats() {
  const { user } = useAuth();
  const [weeklyData, setWeeklyData] = useState<WeeklyDataPoint[]>([]);
  const [dailyData, setDailyData] = useState<DailyDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    // Son 8 haftalık aralık
    const now = new Date();
    const eightWeeksAgo = new Date(now);
    eightWeeksAgo.setDate(eightWeeksAgo.getDate() - 56);
    const startIso = eightWeeksAgo.toISOString();

    // Son 7 günlük aralık (bugün dahil)
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    const dailyStart = sevenDaysAgo.toISOString().split('T')[0];
    const dailyEnd = now.toISOString().split('T')[0];

    const { data: fizboData, error: fizboErr } = await supabase
      .from('ep_fizbo_listings')
      .select('created_at, status, kanban_stage, price')
      .eq('user_id', user.id)
      .gte('created_at', startIso);

    if (fizboErr) {
      setError(fizboErr.message);
      setIsLoading(false);
      return;
    }

    const rows = (fizboData || []) as {
      created_at: string;
      status: string | null;
      kanban_stage: string | null;
      price: number | null;
    }[];

    // --- Haftalık hesaplama ---
    const weeklyMap = new Map<string, WeeklyDataPoint>();
    // Son 8 hafta için boş slot oluştur
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const label = weekLabel(d);
      weeklyMap.set(label, { week: label, kapanan: 0, eklenen: 0, teklif: 0, ciro: 0 });
    }

    rows.forEach((r) => {
      const created = new Date(r.created_at);
      const label = weekLabel(created);
      const existing = weeklyMap.get(label);
      if (!existing) return;

      existing.eklenen += 1;
      if (r.kanban_stage === 'Kapandı') {
        existing.kapanan += 1;
        existing.ciro += Number(r.price) || 0;
      }
      if (r.kanban_stage === 'Teklif') {
        existing.teklif += 1;
      }
    });

    const sortedWeekly = Array.from(weeklyMap.values()).sort(
      (a, b) => parseInt(a.week.replace(/\D/g, '')) - parseInt(b.week.replace(/\D/g, ''))
    );

    // --- Günlük hesaplama ---
    const dailyMap = new Map<string, DailyDataPoint>();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().split('T')[0];
      const dayLabel = DAY_LABELS[d.getDay()];
      dailyMap.set(iso, { day: dayLabel, kapanan: 0, eklenen: 0, teklif: 0, ciro: 0 });
    }

    rows.forEach((r) => {
      const iso = r.created_at.split('T')[0];
      const existing = dailyMap.get(iso);
      if (!existing) return;

      existing.eklenen += 1;
      if (r.kanban_stage === 'Kapandı') {
        existing.kapanan += 1;
        existing.ciro += Number(r.price) || 0;
      }
      if (r.kanban_stage === 'Teklif') {
        existing.teklif += 1;
      }
    });

    const sortedDaily = Array.from(dailyMap.values());

    setWeeklyData(sortedWeekly);
    setDailyData(sortedDaily);
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { weeklyData, dailyData, isLoading, error, refresh: fetchStats };
}